import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, TruckIcon } from 'lucide-react';
import { money } from './QuantityStepper';
import { LIFETIME_UPGRADE_PRICE, SINGLE_PRICE, SINGLE_WITH_LIFETIME_PRICE } from '../data/upsellFlows';
interface LifetimeUpgradeOptionProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Shows the customer-initiated "Want free shipping?" link under the breakdown. */
  showShippingOffer: boolean;
  onShippingOffer: () => void;
}

/**
 * Optional +$19.95 lifetime upgrade on a single X1. Never preselected. When
 * checked it shows the full breakdown (shipping stays calculated at checkout)
 * and a quiet link the customer can tap if they want free shipping.
 */
export function LifetimeUpgradeOption({
  checked,
  onChange,
  showShippingOffer,
  onShippingOffer
}: LifetimeUpgradeOptionProps) {
  return <div className={`border-2 transition-colors duration-150 ease-grit ${checked ? 'gold-edge bg-[#1a1408]' : 'border-white/15 hover:border-white/30'}`} data-id="element-724" data-mp="25w0lglp4kvtu">
      <label className="flex cursor-pointer items-start gap-3 p-4" data-id="element-725" data-mp="f9xq0dq8prwz">
        <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#f2c94c]" data-id="element-726" data-mp="kbdbu808s2gf" />
        <span className="min-w-0 flex-1" data-id="element-727" data-mp="ad7b3tq692d5">
          <span className="flex flex-wrap items-baseline justify-between gap-x-3" data-id="element-728" data-mp="adz8na3bxa62">
            <span className="font-display text-lg font-bold uppercase leading-tight text-[#ffe79a]" data-id="element-729" data-mp="y2iezk2tjcvi">
              Lifetime Replacement Warranty
            </span>
            <span className="font-display text-xl font-extrabold text-offwhite" data-id="element-730" data-mp="oavx54vz6fjs">+{money(LIFETIME_UPGRADE_PRICE)}</span>
          </span>
          <span className="mt-0.5 block font-display text-sm font-bold uppercase tracking-wide text-steel-200" data-id="element-731" data-mp="t979bm9qt256">
            Break it. We replace it.
          </span>
          <span className="mt-1 block text-[13px] font-medium text-steel-400" data-id="element-732" data-mp="1lscrg0j86zf8">
            Upgrade from One-Year Replacement Coverage to Lifetime Replacement Warranty.
          </span>
        </span>
      </label>

      {checked && <dl className="mx-4 grid gap-1 border-t border-white/10 py-3 text-[14px]" data-id="element-733" data-mp="1qp041dwv208a">
          <div className="flex justify-between text-steel-300" data-id="element-734" data-mp="1ml22yxnz6fcl">
            <dt data-id="element-735" data-mp="1hobmi989dhqn">X1 complete system</dt>
            <dd data-id="element-736" data-mp="1hn8ws4mo9lcb">{money(SINGLE_PRICE)}</dd>
          </div>
          <div className="flex justify-between text-steel-300" data-id="element-737" data-mp="1coyxpixtu0lf">
            <dt data-id="element-738" data-mp="1cqa5ruyb1uic">Lifetime Replacement Warranty</dt>
            <dd data-id="element-739" data-mp="17wo04mqk6qw1">+{money(LIFETIME_UPGRADE_PRICE)}</dd>
          </div>
          <div className="flex justify-between font-display text-lg font-extrabold uppercase text-offwhite" data-id="element-740" data-mp="12ycpwawcqeda">
            <dt data-id="element-741" data-mp="yxkg9gjki7gh">Product subtotal</dt>
            <dd data-id="element-742" data-mp="yylsvpg8f20b">{money(SINGLE_WITH_LIFETIME_PRICE)}</dd>
          </div>
          <div className="flex justify-between gap-3 text-steel-300" data-id="element-743" data-mp="ty7l88ijlu2t">
            <dt className="flex items-center gap-1.5" data-id="element-744" data-mp="oyuvng0ae8wj">
              <TruckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-745" data-mp="ozd1938u9zh4" />
              Shipping
            </dt>
            <dd className="text-right font-semibold text-grit-500" data-id="element-746" data-mp="30lyht1ryjlt3">Free U.S. shipping</dd>
          </div>
        </dl>}

      {checked && showShippingOffer && <button type="button" data-buy-intent="true" onClick={onShippingOffer} className="group mx-4 mb-3 flex w-[calc(100%-2rem)] items-center justify-between gap-3 border border-grit-500/70 bg-grit-500/10 px-3.5 py-3 text-left transition-colors duration-150 ease-grit hover:bg-grit-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-747" data-mp="3alfjz9803hkb">
          <span className="font-display text-base font-bold uppercase leading-tight text-grit-500" data-id="element-748" data-mp="2qvln4gqfic98">Want free shipping?</span>
          <span className="flex shrink-0 items-center gap-1 text-[12px] font-bold uppercase tracking-wide text-offwhite" data-id="element-749" data-mp="2vp80yc8bwm6m">
            See your upgrade
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-grit group-hover:translate-x-0.5" aria-hidden="true" data-id="element-750" data-mp="236109mr5muuo" />
          </span>
        </button>}

      <Link to="/legal/guarantee" className="block border-t border-white/10 px-4 py-2.5 text-[12px] font-bold uppercase tracking-[0.16em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-751" data-mp="1f4fin5kzv11f">
        View warranty terms →
      </Link>
    </div>;
}